import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sun, Moon, Clock, Sparkles, AlertTriangle, CalendarDays, Cpu, ChevronRight, MapPin, MessageCircle } from "lucide-react";
import { useSeo } from "@/lib/seo";
import { SiteHeader, SiteFooter } from "@/components/SiteHeader";
import { KA_CITIES } from "@/lib/cities";

const API = process.env.REACT_APP_BACKEND_URL;

const POOJAS = [
  { v: "general", kn: "ಸಾಮಾನ್ಯ ಶುಭ ಕಾರ್ಯ", en: "General auspicious work" },
  { v: "griha-pravesh", kn: "ಗೃಹಪ್ರವೇಶ", en: "Griha Pravesha" },
  { v: "namakarna", kn: "ನಾಮಕರಣ", en: "Naamakarana" },
  { v: "satyanarayan", kn: "ಸತ್ಯನಾರಾಯಣ ಪೂಜೆ", en: "Satyanarayana Pooja" },
  { v: "vivaha", kn: "ವಿವಾಹ", en: "Vivaha" },
];

const Row = ({ kn, en, value, sub, testid }) => (
  <div className="flex items-start justify-between gap-3 border-b border-[#A8722A]/15 py-3 last:border-0" data-testid={testid}>
    <div>
      <p className="font-baloo text-sm text-[#B23A2B]">{kn}</p>
      <p className="font-mukta text-xs text-[#6B5044]">{en}</p>
    </div>
    <div className="text-right">
      <p className="font-eczar text-base font-semibold text-[#3B221B]">{value}</p>
      {sub && <p className="font-mukta text-xs text-[#6B5044]">{sub}</p>}
    </div>
  </div>
);

export default function PanchangaPage() {
  const today = new Date().toLocaleDateString("en-CA");
  const [date, setDate] = useState(today);
  const [cityId, setCityId] = useState("bengaluru");
  const city = KA_CITIES.find((c) => c.id === cityId) || KA_CITIES[0];
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pooja, setPooja] = useState("general");
  const [muhurta, setMuhurta] = useState(null);
  const [mLoading, setMLoading] = useState(false);

  useSeo({
    title: "Panchanga Today — ಪಂಚಾಂಗ for Bengaluru, Mysuru, Mangaluru, Hubballi | PurohithConnect",
    description: "Free live Vedic Panchanga today for Bengaluru, Mysuru, Mangaluru, Hubballi, Udupi & Kalaburagi — tithi, nakshatra, yoga, karana, sunrise/sunset, Rahu Kaala, Abhijit Muhurta and choghadiya. Pick your city for local timings. Find auspicious muhurta for griha pravesha, naamakarana and vivaha. Computed with Swiss Ephemeris (Lahiri sidereal).",
    canonicalPath: "/panchanga",
    jsonLd: [
      { "@context": "https://schema.org", "@type": "WebApplication", name: "PurohithConnect Panchanga & Muhurta Engine", applicationCategory: "LifestyleApplication", operatingSystem: "Web", offers: { "@type": "Offer", price: "0", priceCurrency: "INR" }, description: "Vedic Panchanga and Muhurta for Karnataka cities, computed with Swiss Ephemeris (Lahiri sidereal).", areaServed: ["Bengaluru", "Mysuru", "Mangaluru", "Hubballi", "Udupi", "Kalaburagi", "Karnataka"] },
      { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: [
        { "@type": "Question", name: "How do I see today's panchanga for Mangaluru?", acceptedAnswer: { "@type": "Answer", text: "Open the Panchanga page and pick Mangaluru from the city selector. Its coastal sunrise is later than Bengaluru's, so Rahu Kaala, Abhijit Muhurta and the day's choghadiya are computed for Mangaluru's local sunrise." } },
        { "@type": "Question", name: "Can I get today's panchanga for Mysuru, Hubballi, Udupi or Kalaburagi?", acceptedAnswer: { "@type": "Answer", text: "Yes. Choose Bengaluru, Mysuru, Mangaluru, Hubballi, Udupi or Kalaburagi and the tithi, nakshatra, sunrise/sunset, Rahu Kaala and muhurta windows are computed for that city." } },
      ] },
    ],
  });

  useEffect(() => {
    setLoading(true);
    setMuhurta(null);
    fetch(`${API}/api/panchanga?date=${date}&lat=${city.lat}&lng=${city.lng}`)
      .then((r) => { if (!r.ok) throw new Error("bad"); return r.json(); })
      .then(setP).catch(() => setP(null)).finally(() => setLoading(false));
  }, [date, city.lat, city.lng]);

  const findMuhurta = () => {
    setMLoading(true);
    fetch(`${API}/api/panchanga/muhurta?pooja=${pooja}&start=${date}&days=14&lat=${city.lat}&lng=${city.lng}`)
      .then((r) => { if (!r.ok) throw new Error("bad"); return r.json(); })
      .then(setMuhurta).catch(() => setMuhurta(null)).finally(() => setMLoading(false));
  };

  const shareText = p
    ? `🕉️ ಇಂದಿನ ಪಂಚಾಂಗ · Panchanga — ${city.en} (${date})\nTithi: ${p.tithi.paksha} ${p.tithi.name}\nNakshatra: ${p.nakshatra.name}\nSunrise ${p.sunrise} · Sunset ${p.sunset}\nAbhijit Muhurta: ${p.abhijit_muhurta.start}–${p.abhijit_muhurta.end}\nRahu Kaala (avoid): ${p.inauspicious.rahu_kaala.start}–${p.inauspicious.rahu_kaala.end}\n\nFull panchanga: https://purohithconnect.com/panchanga`
    : "";

  const ratingColor = (r) => r === "Auspicious" ? "bg-[#EAF3EC] text-[#2f6b45] border-[#2f6b4533]"
    : r === "Avoid" ? "bg-[#FBEAE7] text-[#B23A2B] border-[#B23A2B33]" : "bg-[#FBEEDA] text-[#8f5f21] border-[#A8722A44]";

  return (
    <div className="min-h-screen bg-[#FBF6EC] text-[#3B221B]" style={{ colorScheme: "light" }}>
      <SiteHeader />
      <main className="mx-auto max-w-6xl px-4 py-12 md:px-8 md:py-16" data-testid="panchanga-page">
        <div className="text-center">
          <p className="inline-flex items-center gap-2 rounded-full border border-[#A8722A]/30 bg-[#FFFDF8] px-4 py-1 font-mukta text-xs text-[#A8722A]">
            <Cpu size={13} /> Deep-tech engine · Swiss Ephemeris (Lahiri sidereal)
          </p>
          <h1 className="mt-3 font-eczar text-4xl font-bold sm:text-5xl">ಪಂಚಾಂಗ · Panchanga & Muhurta</h1>
          <p className="mx-auto mt-3 max-w-2xl font-mukta text-base text-[#6B5044] md:text-lg">
            ಬೆಂಗಳೂರು, ಮೈಸೂರು, ಮಂಗಳೂರು, ಹುಬ್ಬಳ್ಳಿ ಸೇರಿದಂತೆ ಕರ್ನಾಟಕದ ನಗರಗಳಿಗೆ ನಿಖರ ಪಂಚಾಂಗ. · Accurate, live-computed panchanga and auspicious timings for cities across Karnataka — pick yours.
          </p>
        </div>

        <div className="mx-auto mt-8 flex max-w-2xl flex-wrap items-center justify-center gap-3">
          <div className="inline-flex items-center gap-1.5 rounded-[12px] border border-[#A8722A]/30 bg-[#FFFDF8] pl-3 pr-1">
            <MapPin size={15} className="text-[#A8722A]" />
            <select value={cityId} onChange={(e) => setCityId(e.target.value)} data-testid="panchanga-city"
              className="bg-transparent py-2 pr-1 font-mukta text-sm font-semibold text-[#3B221B] outline-none">
              {KA_CITIES.map((c) => <option key={c.id} value={c.id}>{c.en} · {c.kn}</option>)}
            </select>
          </div>
          <input id="pdate" type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="panchanga-date"
            className="rounded-[12px] border border-[#A8722A]/30 bg-[#FFFDF8] px-4 py-2 font-mukta text-sm outline-none focus:border-[#B23A2B]" />
          <button onClick={() => setDate(today)} data-testid="panchanga-today-btn" className="font-mukta text-sm font-semibold text-[#B23A2B] hover:underline">Today</button>
          {p && (
            <a href={`https://wa.me/?text=${encodeURIComponent(shareText)}`} target="_blank" rel="noopener noreferrer" data-testid="panchanga-whatsapp-share"
              className="inline-flex items-center gap-1.5 rounded-full border border-[#3E6B4F]/40 px-4 py-2 font-mukta text-sm font-semibold text-[#3E6B4F] transition-colors hover:bg-[#3E6B4F] hover:text-white">
              <MessageCircle size={15} /> Share
            </a>
          )}
        </div>

        {loading ? (
          <p className="mt-10 text-center font-mukta text-[#6B5044]" data-testid="panchanga-loading">Computing panchanga…</p>
        ) : !p ? (
          <p className="mt-10 text-center font-mukta text-[#B23A2B]">Could not load panchanga.</p>
        ) : (
          <div className="mt-10 grid gap-6 lg:grid-cols-3">
            <div className="rounded-[18px] border border-[#A8722A]/25 bg-[#FFFDF8] p-6 shadow-sm lg:col-span-2" data-testid="panchanga-main">
              <div className="flex items-center justify-between">
                <h2 className="font-eczar text-2xl font-semibold">{p.vara_kn} · {p.vara}</h2>
                <div className="flex items-center gap-4 font-mukta text-sm text-[#6B5044]">
                  <span className="inline-flex items-center gap-1"><Sun size={15} className="text-[#A8722A]" /> {p.sunrise}</span>
                  <span className="inline-flex items-center gap-1"><Moon size={15} className="text-[#A8722A]" /> {p.sunset}</span>
                </div>
              </div>
              <div className="mt-4">
                <Row kn={`ತಿಥಿ · ${p.tithi.paksha_kn} ${p.tithi.name_kn}`} en={`Tithi · ${p.tithi.paksha} ${p.tithi.name}`} value={p.tithi.name} sub={p.tithi.ends_at ? `upto ${p.tithi.ends_at}` : null} testid="row-tithi" />
                <Row kn={`ನಕ್ಷತ್ರ · ${p.nakshatra.name_kn}`} en={`Nakshatra · pada ${p.nakshatra.pada}`} value={p.nakshatra.name} sub={p.nakshatra.ends_at ? `upto ${p.nakshatra.ends_at}` : null} testid="row-nakshatra" />
                <Row kn="ಯೋಗ" en="Yoga" value={p.yoga.name} sub={p.yoga.ends_at ? `upto ${p.yoga.ends_at}` : null} testid="row-yoga" />
                <Row kn="ಕರಣ" en="Karana" value={p.karana.name} testid="row-karana" />
              </div>
              <div className="mt-5 rounded-[12px] border border-[#2f6b4533] bg-[#EAF3EC] p-4" data-testid="abhijit-card">
                <p className="inline-flex items-center gap-2 font-baloo text-sm text-[#2f6b45]"><Sparkles size={15} /> ಅಭಿಜಿತ್ ಮುಹೂರ್ತ · Abhijit Muhurta (most auspicious)</p>
                <p className="mt-1 font-eczar text-lg font-semibold">{p.abhijit_muhurta.start} – {p.abhijit_muhurta.end}</p>
              </div>
            </div>

            <div className="rounded-[18px] border border-[#B23A2B]/25 bg-[#FBEAE7]/40 p-6" data-testid="panchanga-inauspicious">
              <h3 className="inline-flex items-center gap-2 font-eczar text-lg font-semibold text-[#B23A2B]"><AlertTriangle size={17} /> ವರ್ಜ್ಯ ಕಾಲ · Avoid</h3>
              <div className="mt-3 space-y-3 font-mukta text-sm">
                <div><p className="font-semibold">ರಾಹು ಕಾಲ · Rahu Kaala</p><p className="text-[#6B5044]">{p.inauspicious.rahu_kaala.start} – {p.inauspicious.rahu_kaala.end}</p></div>
                <div><p className="font-semibold">ಯಮಗಂಡ · Yamaganda</p><p className="text-[#6B5044]">{p.inauspicious.yamaganda.start} – {p.inauspicious.yamaganda.end}</p></div>
                <div><p className="font-semibold">ಗುಳಿಕ ಕಾಲ · Gulika Kaala</p><p className="text-[#6B5044]">{p.inauspicious.gulika_kaala.start} – {p.inauspicious.gulika_kaala.end}</p></div>
              </div>
            </div>

            <div className="rounded-[18px] border border-[#A8722A]/25 bg-[#FFFDF8] p-6 lg:col-span-3" data-testid="panchanga-choghadiya">
              <h3 className="inline-flex items-center gap-2 font-eczar text-lg font-semibold"><Clock size={17} className="text-[#A8722A]" /> ದಿನದ ಚೌಘಡಿಯ · Day Choghadiya</h3>
              <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-8">
                {p.choghadiya_day.map((c, i) => (
                  <div key={i} className={`rounded-[10px] border p-2 text-center ${c.good ? "border-[#2f6b4533] bg-[#EAF3EC]" : "border-[#B23A2B22] bg-[#FBEAE7]/50"}`} data-testid={`chogh-${i}`}>
                    <p className={`font-baloo text-xs ${c.good ? "text-[#2f6b45]" : "text-[#B23A2B]"}`}>{c.name_kn}</p>
                    <p className="font-mukta text-[11px] font-semibold text-[#3B221B]">{c.name}</p>
                    <p className="font-mukta text-[10px] text-[#6B5044]">{c.start}</p>
                  </div>
                ))}
              </div>
              <p className="mt-3 font-mukta text-xs text-[#6B5044]">ಹಸಿರು = ಶುಭ (ಅಮೃತ, ಶುಭ, ಲಾಭ, ಚರ) · Green = auspicious choghadiya.</p>
            </div>
          </div>
        )}

        {/* Muhurta finder */}
        <div className="mt-14 rounded-[20px] border border-[#A8722A]/25 bg-gradient-to-br from-[#F6ECDB] to-[#FBF6EC] p-6 md:p-8" data-testid="muhurta-finder">
          <h2 className="inline-flex items-center gap-2 font-eczar text-2xl font-semibold"><CalendarDays size={20} className="text-[#A8722A]" /> ಮುಹೂರ್ತ ಹುಡುಕಿ · Find an Auspicious Muhurta</h2>
          <p className="mt-1 font-mukta text-sm text-[#6B5044]">Next 14 days from the selected date, rated by tithi & nakshatra.</p>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <select value={pooja} onChange={(e) => setPooja(e.target.value)} data-testid="muhurta-pooja"
              className="rounded-[12px] border border-[#A8722A]/30 bg-[#FFFDF8] px-4 py-2.5 font-mukta text-sm outline-none focus:border-[#B23A2B]">
              {POOJAS.map((o) => <option key={o.v} value={o.v}>{o.kn} · {o.en}</option>)}
            </select>
            <button onClick={findMuhurta} disabled={mLoading} data-testid="muhurta-find-btn"
              className="inline-flex items-center gap-2 rounded-full bg-[#B23A2B] px-6 py-2.5 font-mukta font-semibold text-[#FFFDF8] transition-colors hover:bg-[#9a2f22] disabled:opacity-60">
              {mLoading ? "Finding…" : "Find muhurta"} <ChevronRight size={16} />
            </button>
          </div>
          {muhurta && (
            <div className="mt-6 space-y-2" data-testid="muhurta-results">
              {muhurta.results.map((r) => (
                <div key={r.date} className="flex flex-col gap-2 rounded-[12px] border border-[#A8722A]/20 bg-[#FFFDF8] p-4 sm:flex-row sm:items-center sm:justify-between" data-testid={`muhurta-day-${r.date}`}>
                  <div className="flex items-center gap-3">
                    <span className={`shrink-0 whitespace-nowrap rounded-full border px-3 py-1 font-mukta text-xs font-semibold ${ratingColor(r.rating)}`}>{r.rating_kn} · {r.rating}</span>
                    <div>
                      <p className="font-eczar text-sm font-semibold">{new Date(r.date).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" })} · {r.vara}</p>
                      <p className="font-mukta text-xs text-[#6B5044]">{r.paksha} {r.tithi_kn} · {r.nakshatra_kn} ({r.nakshatra})</p>
                    </div>
                  </div>
                  {r.rating !== "Avoid" && (
                    <div className="flex flex-wrap gap-1.5">
                      {r.good_windows.slice(0, 3).map((w, i) => (
                        <span key={i} className="rounded-md bg-[#EAF3EC] px-2 py-1 font-mukta text-[11px] text-[#2f6b45]">{w.label}: {w.start}–{w.end}</span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <p className="mt-8 text-center font-mukta text-xs text-[#6B5044]">
          Computed live with the Swiss Ephemeris (Moshier) using Lahiri ayanamsa — no external API, works offline. For critical ceremonies, confirm with your family purohith. <Link to="/panchanga" className="text-[#B23A2B] hover:underline">·</Link> <Link to="/heritage" className="text-[#B23A2B] hover:underline">Explore our heritage archive →</Link>
        </p>
      </main>
      <SiteFooter />
    </div>
  );
}
